/**
 * Instrument Helpers
 *
 * Shared factory functions and utilities for creating instrument configurations.
 * These helpers eliminate code duplication across instrument implementations.
 */

import { EndpointBuilder, BaseQueryFn } from '@reduxjs/toolkit/query';
import { capitalizeFirst } from '@/utils/strings';
import { InstrumentConfig, InstrumentEndpoints, InstrumentTransforms, InstrumentCacheConfig } from '../types/config';
import { InstrumentFilters, InstrumentSearchParams, SearchResponse, InstrumentType } from '../types';

/**
 * Generate RTK Query cache tag from instrument type
 * e.g., 'funds' -> 'ScreenerFunds'
 */
export function getTagForInstrument(type: InstrumentType): string {
  const capitalized = capitalizeFirst(type);
  return `Screener${capitalized}`;
}

/**
 * Default cache configuration for all instruments
 * Can be overridden per instrument if needed
 */
export const DEFAULT_CACHE_CONFIG: InstrumentCacheConfig = {
  keepUnusedDataFor: 300, // 5 minutes
  refetchOnMountOrArgChange: 30, // Refetch if older than 30 seconds
  refetchOnFocus: true,
  refetchOnReconnect: true,
};

/**
 * Factory to create standard transform functions for an instrument
 * Handles the common pattern of extracting data from ApiResponse
 */
export function createStandardTransforms<TEntity, TFilters extends InstrumentFilters>(): InstrumentTransforms<TEntity, TFilters> {
  return {
    transformFilters: (response: TFilters): TFilters => response || ({} as TFilters),

    transformSearch: (response: SearchResponse<TEntity>): SearchResponse<TEntity> => {
      const data = response?.data || [];
      const pagination = response?.pagination || {
        page: 1,
        limit: data.length,
        total: data.length,
        hasNextPage: false,
      };

      return {
        data,
        pagination,
      };
    },
  };
}

/**
 * Factory to create standard RTK Query endpoint builders
 * Handles the actual RTK Query endpoint configuration
 */
export function createStandardEndpoints<TEntity, TFilters extends InstrumentFilters, TSearchParams extends InstrumentSearchParams>(
  config: Pick<InstrumentConfig<TEntity, TFilters, TSearchParams>, 'endpoints' | 'type' | 'transforms' | 'cache'>,
) {
  const providesTags = [getTagForInstrument(config.type)];
  const cacheConfig = config.cache || {};

  return (builder: EndpointBuilder<BaseQueryFn, string, string>) => ({
    getFilters: builder.query<TFilters, { locale?: string }>({
      query: ({ locale = 'en-CA' }) => ({
        url: config.endpoints.filters,
        params: { locale },
      }),
      providesTags,
      transformResponse: config.transforms?.transformFilters,
      ...cacheConfig,
    }),

    search: builder.query<SearchResponse<TEntity>, TSearchParams>({
      query: (params: TSearchParams) => {
        const { sorting, q, ...restParams } = params;
        const queryParams: Record<string, unknown> = { ...restParams };

        // Transform sorting array to backend's 'sort' parameter (comma-separated for multi-field sorting)
        if (sorting && sorting.length > 0) {
          queryParams.sort = sorting.map(({ id, desc }) => `${id} ${desc ? 'desc' : 'asc'}`).join(',');
        }

        // Rename 'q' to 'query' for backend compatibility
        if (q !== undefined) {
          queryParams.query = q;
        }

        return {
          url: config.endpoints.search,
          params: queryParams,
        };
      },
      providesTags,
      transformResponse: config.transforms?.transformSearch,
      ...cacheConfig,
    }),
  });
}

/**
 * Factory to create a complete instrument configuration
 * This is the simplest way to create an instrument - just provide the basic info
 */
export function createInstrumentConfig<TEntity, TFilters extends InstrumentFilters, TSearchParams extends InstrumentSearchParams>(config: {
  type: InstrumentConfig['type'];
  endpoints: InstrumentEndpoints;
  cache?: InstrumentCacheConfig;
}): InstrumentConfig<TEntity, TFilters, TSearchParams> {
  const transforms = createStandardTransforms<TEntity, TFilters>();
  const cache = config.cache || DEFAULT_CACHE_CONFIG;

  const { type, endpoints } = config;
  return {
    type,
    endpoints,
    cache,
    transforms,
    buildEndpoints: builder =>
      createStandardEndpoints<TEntity, TFilters, TSearchParams>({
        endpoints,
        type,
        transforms,
        cache,
      })(builder),
  };
}
